
import { createSlice, createSelector, nanoid } from "@reduxjs/toolkit";
import imageData from "../shared/imageData.json";
import colorData from "../shared/colorData.json";
import syncedStorage from "../services/syncedStorage";
import { isAlive, liveRows, stampRow, tombstoneFor } from "../utils/syncMeta";
import secureLogger from '../utils/secureLogger';
import subscriptionService from "../services/subscriptionService";
import userService from "../services/userService";
import { generateInvoiceNumber } from "../utils/invoiceNumberGenerator";
import {
  INVOICES_KEY,
  DEFAULT_INVOICE_COLOR,
  DEFAULT_INVOICE_BG,
  INVOICE_DETAILS,
  INVOICE_FORM_KEY,
} from "../constants/localKeys";

// Sum of all unpaid invoices
export const getTotalDueAmount = (state) => {
  try {
    const total = state.invoices.data.reduce((prev, next) => {
      if (!isAlive(next)) return prev;
      if (next.statusName === "Unpaid") {
        const amount = parseFloat(next.totalAmount) || 0;
        return prev + amount;
      }
      return prev;
    }, 0);
    return parseFloat(total.toFixed(2));
  } catch (error) {
    secureLogger.error("Error calculating total due amount:", error);
    return 0;
  }
};

// Sum of all paid invoices
export const getTotalCollectedAmount = (state) => {
  try {
    const total = state.invoices.data.reduce((prev, next) => {
      if (!isAlive(next)) return prev;
      if (next.statusName === "Paid") {
        const amount = parseFloat(next.totalAmount) || 0;
        return prev + amount;
      }
      return prev;
    }, 0);
    return parseFloat(total.toFixed(2));
  } catch (error) {
    secureLogger.error("Error calculating total collected amount:", error);
    return 0;
  }
};

// Helper function to check if a date is within range
const isDateInRange = (dateString, startDate, endDate) => {
  if (!dateString || !startDate || !endDate) return true;
  
  const date = new Date(dateString);
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Set time to start/end of day for proper comparison
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  
  return date >= start && date <= end;
};

// Sum of unpaid invoices within date range (filtered by createdDate)
export const getTotalDueAmountByDateRange = (startDate, endDate) => (state) => {
  try {
    const total = state.invoices.data.reduce((prev, next) => {
      if (!isAlive(next)) return prev;
      if (next.statusName === "Unpaid" && isDateInRange(next.createdDate, startDate, endDate)) {
        const amount = parseFloat(next.totalAmount) || 0;
        return prev + amount;
      }
      return prev;
    }, 0);
    return parseFloat(total.toFixed(2));
  } catch (error) {
    secureLogger.error("Error calculating total due amount by date range:", error);
    return 0;
  }
};

// Sum of paid invoices within date range (filtered by createdDate)
export const getTotalCollectedAmountByDateRange = (startDate, endDate) => (state) => {
  try {
    const total = state.invoices.data.reduce((prev, next) => {
      if (!isAlive(next)) return prev;
      if (next.statusName === "Paid" && isDateInRange(next.createdDate, startDate, endDate)) {
        const amount = parseFloat(next.totalAmount) || 0;
        return prev + amount;
      }
      return prev;
    }, 0);
    return parseFloat(total.toFixed(2));
  } catch (error) {
    secureLogger.error("Error calculating total collected amount:", error);
    return 0;
  }
};

// Compute default dates: created now, due 30 days later
const __now = new Date();
const __dueIn30Days = new Date(__now.getTime() + 30 * 24 * 60 * 60 * 1000);

const initialState = {
  isConfirmModal: false,
  isConfirm: false,
  settingOpen: false,
  defaultColor: colorData[0],
  defaultBgImage: imageData[0],
  colors: colorData,
  images: imageData,
  data: [],
  detailList: [],
  deletedID: null,
  currentEditedID: null,
  newForm: {
    id: nanoid(),
    invoiceNo: "",
    statusIndex: "1",
    statusName: "Draft",
    totalAmount: 1200,
    color: colorData[0],
    backgroundImage: imageData[0],
    dueDate: __dueIn30Days,
    createdDate: __now,
    currencyUnit: "₹",
    clientDetail: {
      id: "",
      name: "",
      mobileNo: "",
      email: "",
      image: "",
      billingAddress: "",
    },
    products: [
      {
        amount: 1200,
        id: "D9vPlvwg11cxYJToEf3x4",
        name: "productName",
        productID: "",
        quantity: 1,
      },
    ],
    taxes: [],
  },
};

export const invoiceSlice = createSlice({
  name: "invoices",
  initialState,
  reducers: {
    setAllInvoice: (state, action) => {
      let invoiceData = action.payload || [];
      
      // Apply subscription limits to existing data
      const user = userService.getCurrentUser();
      if (user) {
        const plans = subscriptionService.getSubscriptionPlans();
        const effectiveTier = subscriptionService.getEffectiveSubscriptionTier();
        const currentPlan = plans[effectiveTier] || plans.free;
        const invoiceLimit = currentPlan.limitations.invoicesSaveExport;
        
        // If user has a limit and data exceeds it, truncate the data
        if (invoiceLimit !== -1 && invoiceData.length > invoiceLimit) {
          console.log(`INVOICE LIMIT ENFORCEMENT: Truncating ${invoiceData.length} invoices to ${invoiceLimit} for ${currentPlan.name} plan`);
          invoiceData = invoiceData.slice(0, invoiceLimit);
        }
      }
      
      state.data = [...invoiceData];
    },

    setAllInvoiceDetailList: (state, action) => {
      let invoiceDetailData = action.payload || [];
      
      // Apply subscription limits to existing data
      const user = userService.getCurrentUser();
      if (user) {
        const plans = subscriptionService.getSubscriptionPlans();
        const effectiveTier = subscriptionService.getEffectiveSubscriptionTier();
        const currentPlan = plans[effectiveTier] || plans.free;
        const invoiceLimit = currentPlan.limitations.invoicesSaveExport;
        
        // If user has a limit and data exceeds it, truncate the data
        if (invoiceLimit !== -1 && invoiceDetailData.length > invoiceLimit) {
          console.log(`INVOICE DETAIL LIMIT ENFORCEMENT: Truncating ${invoiceDetailData.length} invoice details to ${invoiceLimit} for ${currentPlan.name} plan`);
          invoiceDetailData = invoiceDetailData.slice(0, invoiceLimit);
        }
      }
      
      try {
        // Validate and clean invoice data to prevent corruption issues.
        // Tombstones (id + _deleted, no invoiceNo) pass through untouched —
        // they must survive hydration so the next sync upload can propagate
        // the delete to other devices.
        const validatedInvoices = invoiceDetailData.filter(invoice => {
          if (!invoice?.id) return false;
          if (invoice._deleted) return true; // tombstone — keep
          if (!invoice.invoiceNo) {
            console.warn('Invalid invoice detected during load, skipping:', invoice?.id || 'unknown');
            return false;
          }
          return true;
        }).map(invoice => {
          if (invoice._deleted) return invoice; // don't enrich tombstones
          // Ensure all required fields have default values
          return {
            ...invoice,
            statusIndex: invoice.statusIndex || "1",
            statusName: invoice.statusName || "Draft",
            totalAmount: invoice.totalAmount || 0,
            dueDate: invoice.dueDate ? new Date(invoice.dueDate) : new Date(),
            createdDate: invoice.createdDate ? new Date(invoice.createdDate) : new Date(),
            clientDetail: invoice.clientDetail || { name: "", email: "", mobileNo: "", billingAddress: "" },
            companyDetail: invoice.companyDetail || {},
            products: Array.isArray(invoice.products) ? invoice.products : [],
            taxes: Array.isArray(invoice.taxes) ? invoice.taxes : []
          };
        });

        state.detailList = [...validatedInvoices];
        
      } catch (error) {
        console.error('Error validating invoice detail list:', error);
        state.detailList = [];
      }
    },

    setNewInvoices: (state, action) => {
      const user = userService.getCurrentUser();
      if (!user) {
        console.log('INVOICE CREATION BLOCKED: No user found');
        return;
      }
      
      // Note: Subscription limits are checked in the UI layer using canCreateResource()
      // which gets real-time counts from Supabase, similar to how exports work
      // No need for a Redux-level check against local state that could be stale
      
      const { payload } = action;

      // Use existing ID from payload if available, otherwise generate new one
      const id = payload.id || nanoid();

      const {
        invoiceNo,
        statusIndex,
        statusName,
        totalAmount,
        dueDate,
        createdDate,
        clientDetail,
        companyDetail,
      } = payload;

      // Auto-generate invoice number if not provided or empty
      let finalInvoiceNo = invoiceNo;
      if (!finalInvoiceNo || finalInvoiceNo.trim() === '') {
        const companyName = companyDetail?.companyName || 'Invoice';
        // Use detailList which contains full invoice details including invoiceNo
        const existingInvoices = state.detailList || [];
        finalInvoiceNo = generateInvoiceNumber(companyName, existingInvoices, createdDate || new Date());
      }

      // Check for duplicate invoice numbers and handle them
      const existingInvoiceWithSameNumber = state.detailList.find(inv => 
        inv.invoiceNo === finalInvoiceNo && inv.id !== id
      );
      if (existingInvoiceWithSameNumber) {
        console.warn('Duplicate invoice number detected:', finalInvoiceNo);
        // Append a unique suffix if duplicate found
        const timestamp = Date.now().toString().slice(-4);
        finalInvoiceNo = `${finalInvoiceNo}-${timestamp}`;
      }

      const newInvoice = stampRow({
        id,
        invoiceNo: finalInvoiceNo,
        statusIndex,
        statusName,
        totalAmount,
        dueDate,
        createdDate,
        clientName: clientDetail?.name,
      });

      const updateState = [...state.data, newInvoice];
      state.data = updateState;

      // Ensure we create the detail entry with the correct ID and invoice number
      const newDetailEntry = stampRow({
        ...payload,
        id,  // Ensure this matches the ID used in the data array
        invoiceNo: finalInvoiceNo,  // Ensure this matches the generated invoice number
      });
      
      console.log('Creating new invoice detail entry:', {
        id,
        invoiceNo: finalInvoiceNo,
        originalPayloadId: payload.id,
        products: payload.products, // Add this to track product data
        hasProducts: !!payload.products?.length,
        hasTaxes: !!payload.taxes?.length
      });
      
      const newDetailList = [...state.detailList, newDetailEntry];
      state.detailList = newDetailList;
      
      // Save both arrays to localStorage atomically to prevent inconsistency
      try {
        Promise.all([
          syncedStorage.setItem(INVOICES_KEY, updateState),
          syncedStorage.setItem(INVOICE_DETAILS, newDetailList)
        ]).catch(error => {
          console.error('Error saving invoice data to localStorage:', error);
        });
      } catch (error) {
        console.error('Error in invoice localStorage save:', error);
      }

      // Increment usage count in Supabase
      console.log(`📝 Attempting to increment invoices count for ID: ${id}`);
      userService.incrementResourceUsage('invoices', id)
        .then((result) => {
          console.log('✅ Invoice usage count incremented in Supabase:', result);
        })
        .catch(error => {
          console.error('❌ Failed to increment invoice usage count:', error);
        });
    },

    setDefaultColor: (state, action) => {
      const newColor = action.payload;
      state.defaultColor = newColor;
      syncedStorage.setItem(DEFAULT_INVOICE_COLOR, newColor);
    },

    setDefaultBackground: (state, action) => {
      const newBackground = action.payload;
      state.defaultBgImage = newBackground;
      syncedStorage.setItem(DEFAULT_INVOICE_BG, newBackground);
    },

    setDeleteId: (state, action) => {
      state.deletedID = action.payload;
    },

    setEditedId: (state, action) => {
      state.currentEditedID = action.payload;
    },

    onConfirmDeletedInvoice: (state, action) => {
      const deletedInvoice = state.data.find(invoice => invoice.id === state.deletedID);
      // Soft delete with tombstone in both arrays so the deletion survives
      // a per-row merge from another device (without tombstones, the row
      // would resurrect on next sync from a device that hadn't seen the delete).
      const newDatas = state.data.map((invoice) =>
        invoice.id === state.deletedID ? tombstoneFor(invoice.id) : invoice
      );
      state.data = newDatas;

      const newDetails = state.detailList.map((invoice) =>
        invoice.id === state.deletedID ? tombstoneFor(invoice.id) : invoice
      );
      state.detailList = newDetails;

      state.deletedID = null;
      syncedStorage.setItem(INVOICES_KEY, newDatas);
      syncedStorage.setItem(INVOICE_DETAILS, newDetails);

      // Decrement usage count in Supabase
      if (deletedInvoice) {
        userService.decrementResourceUsage('invoices', deletedInvoice.id)
          .then(() => {
            console.log('Invoice usage count decremented in Supabase');
          })
          .catch(error => {
            console.error('Failed to decrement invoice usage count:', error);
          });
      }
    },

    onConfirmEditInvoice: (state, action) => {
      const isFindIndex = state.data.findIndex(
        (product) => product.id === state.currentEditedID
      );
      if (isFindIndex !== -1) {
        state.data[isFindIndex] = stampRow({ ...action.payload });
      }
      state.currentEditedID = null;
      syncedStorage.setItem(INVOICES_KEY, [...state.data]);
    },

    updateNewInvoiceFormField: (state, action) => {
      state.newForm[action.payload.key] = action.payload.value;
      const newForm = { ...state.newForm };
      // JSON round-trip preserved from prior behavior (strips Proxy/undefined
      // before sanitize); changing it could surprise downstream consumers.
      syncedStorage.setItem(
        INVOICE_FORM_KEY,
        JSON.parse(JSON.stringify(newForm))
      );
    },

    updateNewInvoiceForm: (state, action) => {
      state.newForm = { ...action.payload };
      syncedStorage.setItem(INVOICE_FORM_KEY, { ...state.newForm });
    },

    generateInvoiceNo: (state, action) => {
      const { companyName } = action.payload || {};
      if (companyName) {
        // Use detailList which contains full invoice details including invoiceNo
        const existingInvoices = state.detailList || [];
        const newInvoiceNo = generateInvoiceNumber(companyName, existingInvoices);
        state.newForm.invoiceNo = newInvoiceNo;
        syncedStorage.setItem(INVOICE_FORM_KEY, { ...state.newForm });
      }
    },

    resetNewInvoiceForm: (state, action) => {
      // Reset to default form with new ID and empty invoice number
      const created = new Date();
      const dueIn30 = new Date(created.getTime() + 30 * 24 * 60 * 60 * 1000);
      
      state.newForm = {
        id: nanoid(),
        invoiceNo: "",
        statusIndex: "1",
        statusName: "Draft",
        totalAmount: 1200,
        color: colorData[0],
        backgroundImage: imageData[0],
        dueDate: dueIn30,
        createdDate: created,
        currencyUnit: "₹",
        clientDetail: {
          id: "",
          name: "",
          mobileNo: "",
          email: "",
          image: "",
          billingAddress: "",
        },
        products: [
          {
            amount: 1200,
            id: nanoid(),
            name: "productName",
            productID: "",
            quantity: 1,
          },
        ],
        taxes: [],
      };
      syncedStorage.setItem(INVOICE_FORM_KEY, { ...state.newForm });
    },

    updateExisitingInvoiceForm: (state, action) => {
      const {
        id,
        invoiceNo,
        statusIndex,
        statusName,
        totalAmount,
        dueDate,
        createdDate,
        clientDetail,
      } = action.payload;

      const findIndexOfList = state.data.findIndex(
        (product) => product.id === id
      );

      const newInvoice = stampRow({
        id,
        invoiceNo,
        statusIndex,
        statusName,
        totalAmount,
        dueDate,
        createdDate,
        clientName: clientDetail?.name,
      });

      if (findIndexOfList !== -1) {
        state.data[findIndexOfList] = { ...newInvoice };
      }
      const findIndexOfDetail = state.detailList.findIndex(
        (product) => product.id === id
      );

      if (findIndexOfDetail !== -1) {
        // Ensure the detailList is properly updated with all payload data
        state.detailList[findIndexOfDetail] = stampRow({
          ...state.detailList[findIndexOfDetail],
          ...action.payload,
          // Ensure critical fields are properly set
          id,
          invoiceNo,
          statusIndex,
          statusName,
          totalAmount,
          dueDate,
          createdDate,
          clientDetail,
        });
      } else {
        // If for some reason the detail item is not found, add it
        console.warn(`Invoice detail with id ${id} not found in detailList, adding it`);
        state.detailList.push(stampRow({ ...action.payload }));
      }

      // Single atomic pair — the earlier double-write was redundant.
      try {
        Promise.all([
          syncedStorage.setItem(INVOICES_KEY, [...state.data]),
          syncedStorage.setItem(INVOICE_DETAILS, [...state.detailList])
        ]).catch(error => {
          console.error('Error updating invoice data in localStorage:', error);
        });
      } catch (error) {
        console.error('Error in invoice update localStorage save:', error);
      }
    },

    setSettingModalOpen: (state, action) => {
      state.settingOpen = action.payload;
    },

    setConfirmModalOpen: (state, action) => {
      state.isConfirmModal = action.payload;
    },

    setIsConfirm: (state, action) => {
      state.isConfirm = action.payload;
    },
  },
});

export const {
  setAllInvoice,
  setAllInvoiceDetailList,
  setNewInvoices,
  setDefaultColor,
  setDefaultBackground,
  setDeleteId,
  setEditedId,
  setSettingModalOpen,
  setConfirmModalOpen,
  setIsConfirm,
  onConfirmDeletedInvoice,
  onConfirmEditInvoice,
  updateNewInvoiceForm,
  updateNewInvoiceFormField,
  updateExisitingInvoiceForm,
  generateInvoiceNo,
  resetNewInvoiceForm,
} = invoiceSlice.actions;

// Memoize live-row filtering so the derived array keeps reference equality as
// long as the underlying slice array hasn't changed. Plain `(s) => liveRows(s.invoices.data)`
// returns a new `.filter()` array on every call, which makes useSelector warn
// ("Selector returned a different result") and forces unnecessary rerenders.
export const getAllInvoiceSelector = createSelector(
  [(state) => state.invoices.data],
  (data) => liveRows(data)
);

export const getAllColorSelector = (state) => state.invoices.colors;

export const getAllImageSelector = (state) => state.invoices.images;

export const getCurrentBGImage = (state) => state.invoices.defaultBgImage;

export const getCurrentColor = (state) => state.invoices.defaultColor;

export const getAllInvoiceDetailSelector = createSelector(
  [(state) => state.invoices.detailList],
  (detailList) => liveRows(detailList)
);

export const getInvoiceDetailByID = (id) => (state) => {
  const found = state.invoices.detailList.find((detail) => detail.id === id);
  return isAlive(found) ? found : undefined;
};

export const getDeletedInvoiceForm = (state) => state.invoices.deletedID;

export const getInvoiceNewForm = (state) => state.invoices.newForm;

export const getInvoiceSettingModal = (state) => state.invoices.settingOpen;

export const getIsInvoiceConfirmModal = (state) =>
  state.invoices.isConfirmModal;

export const getIsConfirm = (state) => state.invoices.isConfirm;

export const getTotalBalance = (state) => {
  try {
    const total = state.invoices.data.reduce((prev, next) => {
      if (!isAlive(next)) return prev;
      const amount = parseFloat(next.totalAmount) || 0;
      return prev + amount;
    }, 0);
    
    // Format to 2 decimal places and parse back to number
    const formatted = parseFloat(total.toFixed(2));
    return isNaN(formatted) ? 0 : formatted;
  } catch (error) {
            secureLogger.error("Error calculating total balance:", error);
    return 0;
  }
};

export default invoiceSlice.reducer;
